import { useT } from '@transifex/react';
import { useEffect, useState } from 'react';

import { useAssessmentPendingState } from '../../hooks/useAssessmentPendingState';
import { useAssessmentQuestions } from '../../hooks/useAssessmentQuestions';
import type { Answers, AssessmentQuestion, Conditions } from '../../types/assessmentQuestion';
import {
  evaluateQuestionAnswer,
  type EvaluateResult,
  type QuestionAnswerValue,
} from '../../utils/evaluateQuestionAnswer';
import type { ActiveQuestion } from '../../utils/resolveQuestionChoices';

import { GroupedQuestionsScreen } from './components/GroupedQuestionsScreen';
import { SingleQuestionScreen } from './components/SingleQuestionScreen';

type AssessmentContentProps = {
  questions: AssessmentQuestion[];
  conditions?: Conditions;
  /** Called with the full answer map once the group's last question is answered. */
  onContinue: (answers: Answers) => void;
  isSubmitting?: boolean;
  /**
   * Per-assessment testID prefix (e.g. `phq-gad`). Derives stable selectors for
   * the Continue button, the date input, and each answer row — keyed by the active
   * question so option values that repeat across questions (PHQ/GAD scales) stay
   * unique. Omitted → no testIDs.
   */
  testIDPrefix?: string;
  /**
   * Renders every question in `questions` on one scrollable screen instead of
   * today's one-question-per-screen walk: the page's own `pageTitle`/
   * `pageDescription` become the `ScreenHeading`, each question's own title
   * renders as a field label above its input, and a single Continue validates
   * every field at once. Absent or `false` → no change to today's rendering.
   */
  singleScreen?: boolean;
  /** Page-level title. Used as the `ScreenHeading` title when `singleScreen` is
   * true; ignored otherwise (each question's own title is the heading). */
  pageTitle?: string | null;
  /** Page-level description. Used as the `ScreenHeading` subtitle when
   * `singleScreen` is true; ignored otherwise. */
  pageDescription?: string | null;
};

/**
 * RN port of stylos's SurveyPage. Owns the committed `answers` map and decides
 * what a Continue means; everything else is delegated:
 *
 *   - `useAssessmentQuestions` — which question is active, and where an answer
 *     routes next within the group.
 *   - `useAssessmentPendingState` — every question's not-yet-committed input
 *     and its own error copy.
 *   - `evaluateQuestionAnswer` (pure) — whether one question's input is ready
 *     to submit, and what to commit.
 *   - `SingleQuestionScreen` / `GroupedQuestionsScreen` — the two layouts.
 *
 * Unlike Sparta (which auto-advances on choice tap), selecting a choice only
 * highlights it — the member advances with an explicit Continue, for
 * consistency with the rest of the onboarding flow. When the last question is
 * answered, `onContinue` fires.
 */
export function AssessmentContent({
  questions,
  conditions,
  onContinue,
  isSubmitting = false,
  testIDPrefix,
  singleScreen = false,
  pageTitle,
  pageDescription,
}: AssessmentContentProps) {
  const t = useT();
  const [answers, setAnswers] = useState<Answers>({});
  const pending = useAssessmentPendingState();

  // `parsedQuestions` is `useAssessmentQuestions`'s own memoized resolution
  // (keyed on `questions`) — grouped mode needs every question at once, not
  // just "the" active one, but reuses the same value rather than recomputing
  // it on every render (a re-render this component causes on its own, e.g. a
  // keystroke in any field, would otherwise re-run resolveQuestionChoices
  // over the whole node on every character).
  const { activeQuestion, setNextActiveQuestion, parsedQuestions } = useAssessmentQuestions(
    questions,
    conditions
  );

  /**
   * Checks whether `question`'s current pending state is ready to submit, and
   * writes whatever error state that check asks for. The rules themselves are
   * `evaluateQuestionAnswer`'s, which is what lets the grouped Continue reuse
   * them for every question on the page instead of re-deriving the same logic.
   */
  const evaluateQuestion = (question: ActiveQuestion): EvaluateResult => {
    const result = evaluateQuestionAnswer(question, pending.pendingAnswer(question), t);
    pending.applyErrorPatch(question.key, result.errors);
    return result;
  };

  // Commit the answer for `question` into the shared answers map and advance
  // (or finish the group). No pending-state reset is needed — the NEXT
  // question's slot in the pending store has simply never been written, so it
  // already reads as "nothing typed/selected yet" without an explicit clear.
  const commitAnswer = (
    question: ActiveQuestion,
    value: QuestionAnswerValue,
    otherValue?: string
  ) => {
    const nextAnswers: Answers = { ...answers, [question.key]: value };
    if (otherValue && question.otherKey) {
      nextAnswers[question.otherKey] = otherValue;
    }
    setAnswers(nextAnswers);
    setNextActiveQuestion(
      { questionKey: question.key, questionValue: Array.isArray(value) ? '' : value },
      () => onContinue(nextAnswers)
    );
  };

  const onContinuePress = () => {
    if (!activeQuestion) return;
    const result = evaluateQuestion(activeQuestion);
    if (!result.ok) return;
    commitAnswer(activeQuestion, result.value, result.otherValue);
  };

  /**
   * Grouped Continue: validates EVERY question on the page (so every invalid
   * field's own error shows up at once, not just the first) and blocks if any
   * failed, returning that first invalid key for the layout to scroll to — as
   * opposed to the single-question Continue above, which only ever has one
   * question to check. On a clean pass every answer is committed in one go and
   * handed straight to `onContinue`: a grouped page IS the whole node, so
   * there is no next question to route to.
   */
  const onContinuePressGrouped = (): string | null => {
    const results = new Map<string, EvaluateResult>();
    let firstInvalidKey: string | null = null;
    for (const question of parsedQuestions) {
      const result = evaluateQuestion(question);
      results.set(question.key, result);
      if (!result.ok && firstInvalidKey === null) firstInvalidKey = question.key;
    }
    if (firstInvalidKey !== null) return firstInvalidKey;

    const nextAnswers: Answers = { ...answers };
    for (const question of parsedQuestions) {
      const result = results.get(question.key);
      if (!result?.ok) continue;
      nextAnswers[question.key] = result.value;
      if (result.otherValue && question.otherKey) {
        nextAnswers[question.otherKey] = result.otherValue;
      }
    }
    setAnswers(nextAnswers);
    onContinue(nextAnswers);
    return null;
  };

  // Grouped mode: every repeatingList question on the page needs its starting
  // rows up front, since all of them are visible from the first render rather
  // than one at a time. Depending on `parsedQuestions` (a fresh array on every
  // render, per the comment on the other effect) just means this body runs
  // more often than strictly necessary; `seedRows` is idempotent per key, so
  // every run past the first is a no-op.
  useEffect(() => {
    if (!singleScreen) return;
    for (const question of parsedQuestions) pending.seedRows(question);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleScreen, parsedQuestions]);

  // Single-question mode: seed a fresh repeatingList question's starting rows
  // the first time it becomes active. Keyed on `activeQuestion?.key` (the
  // question's own identity), NOT on the `activeQuestion` object itself:
  // `questions` is rebuilt with fresh array + object identities on every
  // parent re-render (DynamicAssessmentFlow calls toDynamicAssessmentQuestions
  // inline, unmemoized), so `activeQuestion` is a new reference on every such
  // re-render even when it's the SAME question. Depending on the object would
  // re-run this effect on every incidental parent re-render (e.g. a
  // rules-engine context update while the member is mid-question); `seedRows`
  // would no-op, but the dependency would still be a lie about what this
  // effect reacts to. `key` only changes when the member actually advances.
  useEffect(() => {
    if (singleScreen || !activeQuestion) return;
    pending.seedRows(activeQuestion);
    // activeQuestion is read from the SAME render whose `key` triggered this
    // run (effects close over the render they were defined in), so it's always
    // in sync with `key` despite not being listed — see the comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeQuestion?.key]);

  if (singleScreen) {
    return (
      <GroupedQuestionsScreen
        questions={parsedQuestions}
        pending={pending}
        isSubmitting={isSubmitting}
        testIDPrefix={testIDPrefix}
        pageTitle={pageTitle}
        pageDescription={pageDescription}
        onContinue={onContinuePressGrouped}
      />
    );
  }

  if (!activeQuestion) return null;

  return (
    <SingleQuestionScreen
      question={activeQuestion}
      pending={pending}
      isSubmitting={isSubmitting}
      testIDPrefix={testIDPrefix}
      onContinue={onContinuePress}
    />
  );
}
